// =====================================================================================
// FILE: src/components/ui/SearchBar.jsx
// Search bar that highlights matching content across rows
// =====================================================================================
import React, { useState, useRef, useEffect } from "react";
import { THEME, FONTS } from "../../constants/theme";

export default function SearchBar({ searchTerm, onSearchChange, matchCount }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);

  // Auto-expand if there's a search term
  useEffect(() => {
    if (searchTerm) setIsExpanded(true);
  }, [searchTerm]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleClose = () => {
    onSearchChange("");
    setIsExpanded(false);
  };

  // Fixed width container to prevent jitter
  const containerStyle = {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    minHeight: 36,
  };

  // Collapsed state - just the icon
  if (!isExpanded) {
    return (
      <div style={containerStyle}>
        <button
          onClick={() => setIsExpanded(true)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "8px 12px",
            background: THEME.surface,
            borderRadius: THEME.radius,
            border: `1px solid ${THEME.line}`,
            cursor: "pointer",
            transition: "border-color 0.15s ease, background 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = THEME.primary;
            e.currentTarget.style.background = THEME.surface2;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = THEME.line;
            e.currentTarget.style.background = THEME.surface;
          }}
          title="Find in view"
        >
          {/* Themed SVG Magnifying Glass */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={THEME.textSecondary}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <span style={{ 
            fontSize: FONTS.sm, 
            color: THEME.textSecondary,
            fontFamily: FONTS.family,
          }}>
            Find
          </span>
        </button>
      </div>
    );
  }

  // Expanded state - full search input (absolute positioned to avoid jitter)
  return (
    <div style={{ ...containerStyle, position: "relative" }}>
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: THEME.surface,
          borderRadius: THEME.radius,
          border: `1px solid ${THEME.primary}`,
          minWidth: 240,
          boxShadow: `0 0 0 3px ${THEME.primary}20, 0 4px 12px rgba(0,0,0,0.1)`,
          zIndex: 100,
        }}
      >
        {/* Themed SVG Magnifying Glass */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke={THEME.primary}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Find in view..."
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: FONTS.sm,
            fontFamily: FONTS.family,
            background: "transparent",
            color: THEME.text,
            minWidth: 120,
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") handleClose();
          }}
        />

        {/* Match Count Badge */}
        {searchTerm && matchCount > 0 && (
          <span
            style={{
              fontSize: FONTS.xs,
              color: THEME.surface,
              background: THEME.dark,
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: FONTS.semibold,
            }}
          >
            {matchCount}
          </span>
        )}

        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 18,
            color: THEME.textSecondary,
            padding: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
          title="Close search"
        >
          ×
        </button>
      </div>
    </div>
  );
}

// =====================================================================================
// HIGHLIGHT HELPER - Use this to wrap text with highlights
// =====================================================================================
export function highlightText(text, searchTerm) {
  if (!searchTerm || !text) return text;
  
  const str = String(text);
  const term = searchTerm.toLowerCase();
  const lowerStr = str.toLowerCase();
  
  if (!lowerStr.includes(term)) return str;
  
  const parts = [];
  let lastIndex = 0;
  let index = lowerStr.indexOf(term);
  
  while (index !== -1) {
    // Add text before match
    if (index > lastIndex) {
      parts.push(str.slice(lastIndex, index));
    }
    
    // Add highlighted match (preserve original case)
    parts.push(
      <mark
        key={index}
        style={{
          background: "#FFEB3B",
          color: THEME.dark,
          padding: "1px 2px",
          borderRadius: 2,
        }}
      >
        {str.slice(index, index + term.length)}
      </mark>
    );
    
    lastIndex = index + term.length;
    index = lowerStr.indexOf(term, lastIndex);
  }
  
  // Add remaining text
  if (lastIndex < str.length) {
    parts.push(str.slice(lastIndex));
  }
  
  return parts;
}

// =====================================================================================
// CHECK IF ROW MATCHES SEARCH
// =====================================================================================
export function rowMatchesSearch(item, searchTerm, assetCache) {
  if (!searchTerm) return false;
  
  const term = searchTerm.toLowerCase();
  
  // Check name
  if (item.name?.toLowerCase().includes(term)) return true;
  
  // Check client/sponsor
  if (item.clientSponsorName?.toLowerCase().includes(term)) return true;
  
  // Check notes
  if (item.notes?.toLowerCase().includes(term)) return true;
  
  // Check club instructions
  if (item.clubInstructions?.toLowerCase().includes(term)) return true;
  
  // Check asset titles (all content columns)
  const assetFields = [
    "bigGfxVideosId", "cornerGfxVideosId", "ledAllTypesId",
    "bigTvcId", "cornerTvcId", "iptvGfxId",
    "superFullId", "super169Id", "superSideId",
    "mcgTiledLedId", "marvelTiledLedId",
    "mcgInterchangeId", "marvelInterchangeId",
    "mcg360Id", "marvel360Id"
  ];
  
  for (const field of assetFields) {
    const assetId = item[field];
    if (assetId && assetCache) {
      const asset = assetCache.get(assetId);
      if (asset?.title?.toLowerCase().includes(term)) return true;
      if (asset?.destination?.toLowerCase().includes(term)) return true;
    }
  }
  
  return false;
}

// =====================================================================================
// COUNT MATCHES IN ITEMS
// =====================================================================================
export function countMatches(items, searchTerm, assetCache) {
  if (!searchTerm) return 0;
  return items.filter(item => rowMatchesSearch(item, searchTerm, assetCache)).length;
}
